import React, { useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { DamageMainCode } from "../../../services/damage/DamageMainCode";
import SuggestionBox from "../../../components/suggestion-box/SuggestionBox";
import FormField from "../../../components/form-field/FormField";
import { RequiredCodeValidatorModel } from "../../../services/form/validators/required/RequiredCodeValidator";
import { NameEntity } from "../../../helpers/NameEntity";

interface EditMainCodeProps {
  value: DamageMainCode | null;
  labelKey?: string;
  mainCodes: DamageMainCode[];
  onChange: (newValue: DamageMainCode | null) => void;
  setFieldValue: Function;
}

export default observer(function EditMainCode(props: EditMainCodeProps) {
  const { value, mainCodes: options, onChange, setFieldValue } = props;
  const [requiredCodeValidator] = useState(() => RequiredCodeValidatorModel.create({}));

  const changeDamageMainCode = useCallback(
    (damageMainCode: NameEntity | undefined) => {
      if (!damageMainCode) {
        onChange(null);
        setFieldValue("mainCode", null);
      } else {
        onChange(damageMainCode as unknown as DamageMainCode);
        setFieldValue("mainCode", damageMainCode);
      }
    },
    [onChange, setFieldValue]
  );

  const handleSearchMainCodeChange = async (searchText: string) => {
    const filteredOptions = options.filter((x) =>
      x.code.toLowerCase().startsWith(searchText.toLowerCase())
    );
    return Promise.resolve(filteredOptions);
  };

  useEffect(() => {
    requiredCodeValidator.set(value);
  }, [requiredCodeValidator, value]);

  const clearMainCodeFieldValue = (text: string) => {
    if (!text) {
      onChange(null);
      setFieldValue("mainCode", null);
  }};

  return (
    <FormField
      id="main-code"
      labelKey={props.labelKey || "general.damage.main-code"}
      fieldName="mainCode"
      fieldValue={value}
      validator={requiredCodeValidator}
      required
      children={() => (
        <SuggestionBox
          defaultInput={value?.code ? `${value.code} ${value.text}` : ""}
          defaultSelectedItem={value?.code ? value : undefined}
          initialOptions={options}
          onSearchTextChange={(searchText) => handleSearchMainCodeChange(searchText)}
          searchTextChanged={clearMainCodeFieldValue}
          selectedItemChanged={changeDamageMainCode}
          placeholderKey="EditDamage.mainCode.suggestion-placeholder"
          placeholderContext={{ label: props.labelKey || "general.damage.main-code" }}
          noResultsFoundKey="EditDamage.mainCode.not-found"
          noResultsFoundContext={{ label: props.labelKey || "general.damage.main-code" }}
          clearCurrentItem={ () => clearMainCodeFieldValue("")}
        />
      )}
      errorName="mainCode"
    />
  );
});
